// =============================================
// Pillar Link Post-Check (STOP-P1 z faza P planu)
//
// Lekki deterministyczny post-processor po generacji pillara LLM:
// 1. Parse HTML — znajdz wszystkie <a href="/slug">text</a> w prozie
// 2. Dedupe duplicate slug — zostaw pierwszy, usun subsequent (zachowaj tekst)
// 3. Brakujace published spoki -> footer <h3>Powiazane artykuly z silosa</h3><ul>
// 4. Refresh thematic_node_relations (hub_to_spoke) — UPSERT linked + DELETE unlinked
//
// Czysty parser HTML (regex), zero LLM call, idempotent (re-run identyczny wynik).
// Helper dedupeAndAppendFooter() = pure function (testowalna bez DB mock).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import {
  logDebug as _logDebugRaw,
  logError as _logErrorRaw,
  logPublic as _logPublicRaw,
} from "./contentLogger";

// P-D (faza P): kategoria "content-engine" zeby pillar runy widac w panelu LOGI.
const logPublic = (prefix: string, msg: string) => _logPublicRaw(prefix, msg, "content-engine");
const logError = (prefix: string, msg: string, err?: unknown) => _logErrorRaw(prefix, msg, err, "content-engine");
const logDebug = (prefix: string, msg: string, data?: Record<string, unknown>) => _logDebugRaw(prefix, msg, data, "content-engine");

// =============================================
// Public types
// =============================================

export interface PillarPostCheckSpoke {
  node_id: string;
  title: string;
  slug_path: string;        // np. "/co-to-jest-growth-experiment" (z prefiksem)
  is_published: boolean;
  wp_post_url: string | null;
}

export interface LinkOccurrence {
  slug: string;             // np. "/co-to-jest-growth-experiment"
  count: number;            // ile razy w HTML PRE-dedupe
  first_occurrence_kept: boolean;
}

export interface OrphanInFooter {
  slug: string;
  title: string;
}

export interface CanonicalizedLink {
  wrong_slug: string;   // co LLM wstawil (np. "/zwykego")
  correct_slug: string; // canonical z wp_post_url (np. "/zwyklego")
  distance: number;     // 0 = match po normalizacji, 1-2 = Levenshtein fix
}

export interface PillarLinkPostCheckResult {
  html: string;
  links_in_prose: LinkOccurrence[];
  deduplicated_count: number;            // ile <a> usunieto (subsequent duplicates)
  orphans: OrphanInFooter[];
  node_relations_refreshed: number;      // ile UPSERT (link present in prose OR footer)
  node_relations_removed: number;        // ile DELETE (spoke not in publishedSpokes anymore)
  // P-F: kanonizacja malformed slugow (LLM przekrecil slug — Levenshtein <=2 do canonical)
  corrected_links: CanonicalizedLink[];  // rewriten /wrong -> /correct
  broken_links: string[];                // /slug NIE pasuje do zadnego canonical (distance >2)
}

// =============================================
// Pure helpers (testowalne bez Supabase mock)
// =============================================

/**
 * Wyciaga relatywny slug z pelnego URL (np. https://growthhacker.pl/foo/ -> /foo).
 * Zwraca null gdy URL invalid.
 */
export function extractSlugFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/([^/]+)\/?$/);
  return match ? `/${match[1]}` : null;
}

/**
 * Regex match wszystkie <a href="/slug">text</a> w HTML.
 * Wraca lista occurrences w document order.
 * Tylko relatywne href (zaczynajace od `/`) — external linki ignored.
 */
export function parseInlineRelativeLinks(html: string): Array<{
  slug: string;
  full_match: string;        // pelny tag <a ...>text</a>
  match_start: number;       // pozycja w HTML
}> {
  const result: Array<{ slug: string; full_match: string; match_start: number }> = [];
  // Match <a href="/anything">text</a> — non-greedy, multiline
  const regex = /<a\s+href="(\/[^"]+)"[^>]*>([^<]*)<\/a>/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(html)) !== null) {
    result.push({
      slug: m[1],
      full_match: m[0],
      match_start: m.index,
    });
  }
  return result;
}

// =============================================
// P-F (faza P hotfix): Levenshtein-based slug canonicalization
// LLM bywa przekrecic slug (np. "/zwykego" zamiast "/zwyklego") -> martwe linki w HTML
// + falszywe sieroty. Fix deterministyczny PRZED dedupe.
// =============================================

/**
 * Levenshtein distance z early bailout dla performance.
 * Zwraca Infinity jesli odleglosc > maxDistance (skip full DP).
 * Pure, deterministyczna (zero side effects).
 */
export function levenshteinDistance(a: string, b: string, maxDistance = Infinity): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > maxDistance) return Infinity;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // DP table — rolling 2 rows for memory eff (m chars vs full m*n table)
  let prev = new Array(b.length + 1).fill(0);
  let curr = new Array(b.length + 1).fill(0);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost, // substitution
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    // Early bailout: gdy cala biezaca row > maxDistance, ostateczny wynik tez bedzie
    if (rowMin > maxDistance) return Infinity;
    [prev, curr] = [curr, prev]; // swap rolling rows
  }

  return prev[b.length];
}

/**
 * Znajdz najblizszy canonical slug w zbiorze (max Levenshtein <= maxDistance).
 * Tie-breaker: lex sort (deterministyczne dla identycznych dystansow).
 * Zwraca { slug, distance } LUB null gdy brak match <= maxDistance.
 */
export function findClosestCanonicalSlug(
  slug: string,
  canonicalSlugs: ReadonlyArray<string>,
  maxDistance = 2,
): { slug: string; distance: number } | null {
  let bestDist = Infinity;
  let bestSlug: string | null = null;
  // Sort canonical lex dla deterministycznego tie-break
  const sortedCanonical = [...canonicalSlugs].sort();
  for (const canon of sortedCanonical) {
    const dist = levenshteinDistance(slug, canon, maxDistance);
    if (dist < bestDist) {
      bestDist = dist;
      bestSlug = canon;
      if (bestDist === 0) break; // exact match, mozna stop
    }
  }
  if (bestSlug === null || bestDist > maxDistance) return null;
  return { slug: bestSlug, distance: bestDist };
}

/**
 * Normalizacja slug — lowercase + strip trailing slash.
 * Dla case-insensitive / trailing-slash mismatch match.
 */
function normalizeSlug(slug: string): string {
  return slug.toLowerCase().replace(/\/+$/, "");
}

/**
 * Canonicalize malformed slugs w HTML pillara.
 * Pure function — czysta transformacja HTML + diagnostyka.
 *
 * Per <a href="/slug"> w prozie:
 *  1. Jesli slug IN canonicalSlugs -> OK, no-op
 *  2. Jesli normalizeSlug(slug) IN canonicalSlugs -> rewrite href (case/trailing fix)
 *  3. Else Levenshtein -> closest canonical, distance <=2 -> rewrite href
 *  4. Else (no match <=2) -> zostaw w HTML + dolacz do broken_links
 *
 * Rewrite: href="/wrong" -> href="/correct" (replaceAll, safe bo regex pasuje cale href="..." z trailing ")
 * Idempotent: 2x call na poprawionym HTML -> 0 corrections drugim razem.
 */
export function canonicalizeMalformedSlugs(
  html: string,
  publishedSpokes: PillarPostCheckSpoke[],
): {
  html: string;
  corrected_links: CanonicalizedLink[];
  broken_links: string[];
} {
  const canonicalSlugs = publishedSpokes
    .filter((s) => s.is_published && s.slug_path)
    .map((s) => s.slug_path);
  if (canonicalSlugs.length === 0) {
    return { html, corrected_links: [], broken_links: [] };
  }
  const canonicalSet = new Set(canonicalSlugs);
  const canonicalNormMap = new Map<string, string>(); // normalized -> canonical (real path)
  for (const c of canonicalSlugs) {
    canonicalNormMap.set(normalizeSlug(c), c);
  }

  const occurrences = parseInlineRelativeLinks(html);
  // Map wrong->correct per unique wrong slug (avoid double-mapping)
  const slugReplacements = new Map<string, { correct: string; distance: number }>();
  const brokenSet = new Set<string>();

  for (const occ of occurrences) {
    if (canonicalSet.has(occ.slug)) continue;       // exact match, OK
    if (slugReplacements.has(occ.slug)) continue;   // already resolved this wrong slug
    if (brokenSet.has(occ.slug)) continue;          // already marked broken

    // Try 1: normalize (lowercase + strip trailing slash)
    const norm = normalizeSlug(occ.slug);
    const normMatch = canonicalNormMap.get(norm);
    if (normMatch && normMatch !== occ.slug) {
      slugReplacements.set(occ.slug, { correct: normMatch, distance: 0 });
      continue;
    }

    // Try 2: Levenshtein <= 2
    const closest = findClosestCanonicalSlug(occ.slug, canonicalSlugs, 2);
    if (closest) {
      slugReplacements.set(occ.slug, { correct: closest.slug, distance: closest.distance });
      continue;
    }

    // Try 3: zero match -> broken link
    brokenSet.add(occ.slug);
  }

  // Apply replacements w HTML — replaceAll href="/wrong" -> href="/correct".
  // Safe: regex parsera matchuje href="..." z " kotwiczacym, wiec replaceAll z full
  // pattern 'href="/wrong"' nie zlapie partial matches (np. innego linku zawierajacego wrong jako substring).
  let resultHtml = html;
  const corrected_links: CanonicalizedLink[] = [];
  for (const [wrong, { correct, distance }] of slugReplacements) {
    resultHtml = resultHtml.replaceAll(`href="${wrong}"`, `href="${correct}"`);
    corrected_links.push({ wrong_slug: wrong, correct_slug: correct, distance });
  }

  return {
    html: resultHtml,
    corrected_links,
    broken_links: [...brokenSet].sort(), // sort dla deterministycznego output (testy stable)
  };
}

/**
 * Pure function: dedupe duplicate <a href> + append footer dla brakujacych published spokes.
 * Bez touchowania DB — czysta transformacja HTML.
 *
 * Algorytm:
 * 1. Parse all <a href="/slug">text</a> occurrences (document order)
 * 2. Per slug count, mark first as "kept", subsequent as "remove" (replace tag z innerText)
 * 3. Compute set of "linked slugs" (kept occurrences)
 * 4. Compute orphans = publishedSpokes.slug_path NOT IN linkedSlugs
 * 5. Append footer if orphans.length > 0
 */
export function dedupeAndAppendFooter(
  html: string,
  publishedSpokes: PillarPostCheckSpoke[],
): {
  html: string;
  links_in_prose: LinkOccurrence[];
  deduplicated_count: number;
  orphans: OrphanInFooter[];
} {
  const occurrences = parseInlineRelativeLinks(html);

  // Group occurrences by slug, preserve document order
  const bySlug = new Map<string, Array<{ full_match: string; match_start: number }>>();
  for (const occ of occurrences) {
    const arr = bySlug.get(occ.slug) ?? [];
    arr.push({ full_match: occ.full_match, match_start: occ.match_start });
    bySlug.set(occ.slug, arr);
  }

  const links_in_prose: LinkOccurrence[] = [];
  let resultHtml = html;
  let deduplicated_count = 0;

  // Dedupe: for each slug with count > 1, remove subsequent <a> tags (keep innerText)
  for (const [slug, occs] of bySlug.entries()) {
    links_in_prose.push({
      slug,
      count: occs.length,
      first_occurrence_kept: true,
    });

    if (occs.length > 1) {
      // Skip first occurrence (kept), replace subsequent N-1 with innerText
      // IMPORTANT: replace order matters — we replace each full_match string,
      // ale `full_match` zawiera ten sam tekst dla wszystkich occurrences tego slug
      // (jesli innerText sie rozni — kazda occurrence ma inny full_match, OK).
      //
      // Strategia: replace 1 occurrence na raz, od KONCA HTML (zeby indeksy nie pojechaly).
      // Faktycznie: ".replace()" replace FIRST occurrence — wiec replace N-1 razy
      // skipujac pierwsza pozycje.
      let workingHtml = resultHtml;
      let replaced = 0;
      for (let i = 1; i < occs.length; i++) {
        // Extract innerText from full_match
        const innerMatch = occs[i].full_match.match(/<a\s+href="[^"]+"[^>]*>([^<]*)<\/a>/i);
        const innerText = innerMatch ? innerMatch[1] : "";
        // Find SECOND or later occurrence — use a placeholder trick:
        // znajdz first occurrence, jesli to ta sama liczba ze i-th, skip; inaczej replace.
        // Simpler: replace all subsequent occurrences in sequence.
        // Faktycznie: jesli occs[1].full_match === occs[0].full_match (ten sam tag, identical innerText),
        // to .replace() w workingHtml zamieni FIRST — co bedzie odpowiadać occs[0]... niedobrze.
        //
        // Bezpieczna strategia: replace KAZDY full_match raz, ale licz ze potem occs[0] (kept)
        // moze sie zlac z roznymi i-th tagami jesli identyczne. Wtedy musimy zostawic 1
        // a usunac N-1 — co `replaceAll().slice(1)` nie zrobi.
        //
        // BEZPIECZNIEJSZA: zlicz wszystkie occurrences danego full_match w HTML, zostaw 1,
        // usun pozostale.
        const count = workingHtml.split(occs[i].full_match).length - 1;
        if (count > 0) {
          // Replace 1 occurrence at a time (first found, ale po replace count maleje).
          // Po N iteracjach wszystkie usuniete.
          workingHtml = workingHtml.replace(occs[i].full_match, innerText);
          replaced++;
        }
      }
      resultHtml = workingHtml;
      deduplicated_count += replaced;
    }
  }

  // Compute orphans = publishedSpokes whose slug NOT in linkedSlugs (occurrences first).
  // P-B (faza P): orphans NIE są renderowane do HTML jako stopka — to footprint.
  // Pole orphans zostaje w wyniku DLA FORENSICS (diagnoza w logach),
  // ale HTML output NIE dostaje <h3>Powiązane artykuły z silosa</h3><ul>...
  // Pokrycie grafu i tak daje PRE-STEP createHubToSpokeRelations (thematic_node_relations).
  const linkedSlugs = new Set(links_in_prose.map((l) => l.slug));
  const orphans: OrphanInFooter[] = [];
  for (const spoke of publishedSpokes) {
    if (!spoke.is_published || !spoke.slug_path) continue;
    if (!linkedSlugs.has(spoke.slug_path)) {
      orphans.push({
        slug: spoke.slug_path,
        title: spoke.title,
      });
    }
  }

  // P-B: STOPKA USUNIETA. Sieroty logujemy jako WARNING (do diagnozy w panelu LOGI),
  // ale HTML output NIE dostaje footera. Distribution outline w P-A powinno
  // zapewnic ze sieroty = 0 (kazdy spoke trafia do dokladnie 1 sekcji).
  // Jesli sieroty istnieja -> sygnal ze LLM nie przestrzegal outline OR matching
  // tytulow zawodzi (P-A logDebug pokazuje unmatched).

  return {
    html: resultHtml,
    links_in_prose,
    deduplicated_count,
    orphans,
  };
}

// =============================================
// Main entry — DB wrapper (UPSERT + DELETE relations)
// =============================================

/**
 * Pelny post-check: dedupe HTML + footer + refresh thematic_node_relations.
 *
 * Idempotent:
 * - Dedupe HTML — czysta funkcja
 * - Relations: DELETE existing hub_to_spoke from pillar PRZED INSERT batch (kazdy run = identyczne relacje)
 */
export async function postCheckPillarLinks(
  html: string,
  pillarNodeId: string,
  pillarMapId: string,
  pillarUserId: string,
  publishedSpokes: PillarPostCheckSpoke[],
): Promise<PillarLinkPostCheckResult> {
  logPublic("PillarPostCheck", `START pillar=${pillarNodeId.slice(0, 8)} spokes=${publishedSpokes.length}`);
  const startTs = Date.now();

  // P-F (faza P hotfix): kanonizacja malformed slugow PRZED dedupe.
  // LLM bywa przekrecic slug (literowka, case, trailing slash) -> martwy link 404 + falszywa sierota.
  // canonicalizeMalformedSlugs naprawia href deterministycznie: normalize -> Levenshtein <=2 -> rewrite.
  // Recompute links_in_prose + orphans w nastepnym kroku (dedupeAndAppendFooter) dzialaja juz
  // na POPRAWIONYM HTML -> sieroty wynikaja tylko z prawdziwych brakow w outline distribution.
  const canonical = canonicalizeMalformedSlugs(html, publishedSpokes);
  if (canonical.corrected_links.length > 0) {
    logPublic(
      "PillarPostCheck",
      `corrected ${canonical.corrected_links.length} malformed slugs (Levenshtein <=2): ${canonical.corrected_links.map((c) => `${c.wrong_slug}->${c.correct_slug}(d=${c.distance})`).slice(0, 5).join(", ")}${canonical.corrected_links.length > 5 ? ", ..." : ""}`,
    );
  }
  if (canonical.broken_links.length > 0) {
    for (const broken of canonical.broken_links) {
      logError("PillarPostCheck", `PILLAR BROKEN LINK: ${broken} nie pasuje do zadnego published spoka (Levenshtein >2)`);
    }
  }

  // 1+2+3+4. Pure HTML transformation (operuje na poprawionym HTML)
  const pure = dedupeAndAppendFooter(canonical.html, publishedSpokes);

  // 5. Compute set of slugs that have presence (prose OR footer)
  const linkedSlugs = new Set<string>([
    ...pure.links_in_prose.map((l) => l.slug),
    ...pure.orphans.map((o) => o.slug),
  ]);

  // 6. Build map slug -> node_id (z publishedSpokes)
  const slugToNodeId = new Map<string, string>();
  for (const spoke of publishedSpokes) {
    if (spoke.is_published && spoke.slug_path) {
      slugToNodeId.set(spoke.slug_path, spoke.node_id);
    }
  }

  // 7. Refresh thematic_node_relations: DELETE existing hub_to_spoke from pillar + INSERT batch
  let node_relations_refreshed = 0;
  let node_relations_removed = 0;
  try {
    // a. Count existing rows (dla raportu "removed" = existing - new)
    const { count: existingCount } = await (supabase as any)
      .from("thematic_node_relations")
      .select("id", { count: "exact", head: true })
      .eq("from_node_id", pillarNodeId)
      .eq("relation_type", "hub_to_spoke");

    // b. DELETE existing (idempotent reset)
    const { error: deleteErr } = await (supabase as any)
      .from("thematic_node_relations")
      .delete()
      .eq("from_node_id", pillarNodeId)
      .eq("relation_type", "hub_to_spoke");

    if (deleteErr) {
      logError("PillarPostCheck", `DELETE relations failed: ${deleteErr.message}`);
    } else {
      node_relations_removed = existingCount ?? 0;
    }

    // c. INSERT batch — jeden row per linked spoke
    const rowsToInsert: Array<{
      user_id: string;
      map_id: string;
      from_node_id: string;
      to_node_id: string;
      relation_type: string;
      weight: number;
    }> = [];

    for (const slug of linkedSlugs) {
      const toNodeId = slugToNodeId.get(slug);
      if (!toNodeId) continue;
      rowsToInsert.push({
        user_id: pillarUserId,
        map_id: pillarMapId,
        from_node_id: pillarNodeId,
        to_node_id: toNodeId,
        relation_type: "hub_to_spoke",
        weight: 1,
      });
    }

    if (rowsToInsert.length > 0) {
      const { error: insertErr } = await (supabase as any)
        .from("thematic_node_relations")
        .insert(rowsToInsert);
      if (insertErr) {
        logError("PillarPostCheck", `INSERT relations failed: ${insertErr.message}`);
      } else {
        node_relations_refreshed = rowsToInsert.length;
      }
    }
  } catch (err) {
    logError("PillarPostCheck", `Relations refresh exception: ${err instanceof Error ? err.message : err}`);
  }

  const durationMs = Date.now() - startTs;
  logPublic(
    "PillarPostCheck",
    `END dedupe=${pure.deduplicated_count} orphans=${pure.orphans.length} (NIE w stopce — forensics only) relations: +${node_relations_refreshed}/-${node_relations_removed} (${durationMs}ms)`,
  );
  logDebug("PillarPostCheck", "links_in_prose", { links: pure.links_in_prose });

  // P-B (faza P): jesli orphans > 0 -> WARNING per spoke (do diagnozy w panelu LOGI).
  // Po P-A per-section distribution outline powinno zapewnic 0 sierot. Jesli sa -> sygnal
  // ze LLM nie przestrzegal outline lub matching tytulow w P-A zawiodl (logDebug pokazuje unmatched).
  if (pure.orphans.length > 0) {
    for (const orphan of pure.orphans) {
      logError("PillarPostCheck", `PILLAR ORPHAN: ${orphan.slug} ("${orphan.title}") nie trafil do prozy (NIE renderowany w HTML — diagnoza: distribution outline lub matching tytulow zawiodl)`);
    }
  }

  return {
    html: pure.html,
    links_in_prose: pure.links_in_prose,
    deduplicated_count: pure.deduplicated_count,
    orphans: pure.orphans,
    node_relations_refreshed,
    node_relations_removed,
    corrected_links: canonical.corrected_links,
    broken_links: canonical.broken_links,
  };
}
